/**
 * ClientDetailPage — full detail view for a single client.
 *
 * Features:
 * - Client info card (name, NIT, contact info, channel, status, owner, created)
 * - Edit client button (opens ClientForm in edit mode)
 * - Accounts section — grid of AccountCards with "Agregar Cuenta" button
 * - Add Account dialog with minimal form (name, plan, timezone)
 * - Billing section (super_admin / sales_admin only):
 *   - Invoice history table with status badges
 *   - Per-account: "Crear Customer Stripe" / "Generar link de pago" buttons
 *   - Credit grant form (collapsible)
 * - BillingRollup — aggregated billing across all accounts (real data from API)
 * - Loading/error states handled via TanStack Query status
 *
 * Hooks: useClient + useAccounts + useClientInvoices + billing mutations
 */

import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useClient, useUpdateClient } from '@/hooks/useClients'
import { useAccounts, useCreateAccount } from '@/hooks/useAccounts'
import {
  useClientInvoices,
  useProvisionStripeCustomer,
  useGeneratePaymentLink,
  useGrantCredit,
} from '@/hooks/useBilling'
import { ClientForm } from '@/components/clients/ClientForm'
import { AccountCard } from '@/components/clients/AccountCard'
import { BillingRollup } from '@/components/clients/BillingRollup'
import { useAuth } from '@/lib/auth'
import type { AccountRow, UpdateClientDTO, CreateAccountDTO } from '@1klick/types'
import type { InvoiceRow } from '@/hooks/useBilling'

// ── Helpers ───────────────────────────────────────────────────────────────────

const CHANNEL_LABELS: Record<string, string> = {
  direct: 'Directo',
  reseller: 'Revendedor',
  freelance: 'Freelance',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-700',
  suspended: 'bg-red-100 text-red-800',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  suspended: 'Suspendido',
}

const INVOICE_STATUS_COLORS: Record<string, string> = {
  paid: 'bg-green-100 text-green-800',
  open: 'bg-blue-100 text-blue-800',
  overdue: 'bg-red-100 text-red-800',
  void: 'bg-gray-100 text-gray-600',
  draft: 'bg-yellow-100 text-yellow-700',
}

const INVOICE_STATUS_LABELS: Record<string, string> = {
  paid: 'Pagada',
  open: 'Abierta',
  overdue: 'Vencida',
  void: 'Anulada',
  draft: 'Borrador',
}

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700'
  const label = STATUS_LABELS[status] ?? status
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}
    >
      {label}
    </span>
  )
}

function InvoiceStatusBadge({ status }: { status: string }) {
  const color = INVOICE_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
  const label = INVOICE_STATUS_LABELS[status] ?? status
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {label}
    </span>
  )
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(cents / 100)
}

// ── New Account Form ──────────────────────────────────────────────────────────

interface NewAccountFormProps {
  onSubmit: (data: CreateAccountDTO) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  error: string | null
}

function NewAccountForm({ onSubmit, onCancel, isSubmitting, error }: NewAccountFormProps) {
  const [name, setName] = useState('')
  const [planId, setPlanId] = useState('')
  const [timezone, setTimezone] = useState('America/Bogota')
  const [demoFlag, setDemoFlag] = useState(false)
  const [notes, setNotes] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      name: name.trim(),
      planId: planId.trim(),
      timezone: timezone.trim() || 'America/Bogota',
      demoFlag,
      notes: notes.trim() || undefined,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label htmlFor="na-name" className="mb-1 block text-sm font-medium text-gray-700">
          Nombre de la cuenta <span className="text-red-500">*</span>
        </label>
        <input
          id="na-name"
          type="text"
          required
          minLength={2}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej. Wok"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div>
        <label htmlFor="na-planId" className="mb-1 block text-sm font-medium text-gray-700">
          ID del plan (UUID) <span className="text-red-500">*</span>
        </label>
        <input
          id="na-planId"
          type="text"
          required
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">
          El UUID del plan asignado a esta cuenta.
        </p>
      </div>

      <div>
        <label htmlFor="na-timezone" className="mb-1 block text-sm font-medium text-gray-700">
          Zona horaria
        </label>
        <select
          id="na-timezone"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="America/Bogota">America/Bogota (COT)</option>
          <option value="America/Mexico_City">America/Mexico_City (CST)</option>
          <option value="America/Lima">America/Lima (PET)</option>
          <option value="America/Santiago">America/Santiago (CLT)</option>
          <option value="America/Buenos_Aires">America/Buenos_Aires (ART)</option>
          <option value="America/New_York">America/New_York (EST)</option>
          <option value="Europe/Madrid">Europe/Madrid (CET)</option>
          <option value="UTC">UTC</option>
        </select>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="na-demo"
          type="checkbox"
          checked={demoFlag}
          onChange={(e) => setDemoFlag(e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="na-demo" className="text-sm text-gray-700">
          Cuenta demo
        </label>
      </div>

      <div>
        <label htmlFor="na-notes" className="mb-1 block text-sm font-medium text-gray-700">
          Notas
        </label>
        <textarea
          id="na-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Notas sobre esta cuenta..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Creando...' : 'Crear cuenta'}
        </button>
      </div>
    </form>
  )
}

// ── Invoice History Table ─────────────────────────────────────────────────────

interface InvoiceTableProps {
  invoices: InvoiceRow[]
  accountNames: Record<string, string>
}

const INITIAL_ROWS = 10

function InvoiceTable({ invoices, accountNames }: InvoiceTableProps) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? invoices : invoices.slice(0, INITIAL_ROWS)

  if (invoices.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-gray-500">Sin facturas registradas</p>
    )
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Fecha
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Cuenta
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Estado
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Monto
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                PDF
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {visible.map((inv) => (
              <tr key={inv.id} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                  {formatShortDate(inv.created_at)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {accountNames[inv.account_id] ?? inv.account_id.slice(0, 8) + '…'}
                </td>
                <td className="px-4 py-3">
                  <InvoiceStatusBadge status={inv.status} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900">
                  {formatCents(inv.amount_cents)}
                </td>
                <td className="px-4 py-3 text-center text-sm">
                  {inv.pdf_url ? (
                    <a
                      href={inv.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Ver PDF
                    </a>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {invoices.length > INITIAL_ROWS && (
        <div className="border-t border-gray-100 px-4 py-3 text-center">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {expanded ? 'Mostrar menos' : `Ver todas (${invoices.length})`}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Account Billing Actions ───────────────────────────────────────────────────

interface AccountBillingActionsProps {
  account: AccountRow
  clientId: string
}

function AccountBillingActions({ account, clientId }: AccountBillingActionsProps) {
  const provisionMutation = useProvisionStripeCustomer(clientId)
  const paymentLinkMutation = useGeneratePaymentLink()

  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [provisionSuccess, setProvisionSuccess] = useState(false)

  const handleProvision = async () => {
    try {
      setProvisionSuccess(false)
      await provisionMutation.mutateAsync(account.id)
      setProvisionSuccess(true)
    } catch {
      // Error displayed via mutation.error
    }
  }

  const handleGenerateLink = async () => {
    try {
      setPaymentUrl(null)
      const result = await paymentLinkMutation.mutateAsync(account.id)
      setPaymentUrl(result.url)
    } catch {
      // Error displayed via mutation.error
    }
  }

  const handleCopy = () => {
    if (paymentUrl) {
      window.navigator.clipboard.writeText(paymentUrl).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {!account.stripe_customer_id ? (
        // Account has no Stripe Customer — show provision button
        <div>
          <button
            type="button"
            onClick={handleProvision}
            disabled={provisionMutation.isPending}
            className="rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {provisionMutation.isPending ? '...' : 'Crear Customer Stripe'}
          </button>
          {provisionMutation.isError && (
            <p className="mt-1 text-xs text-red-600">
              {provisionMutation.error?.message ?? 'Error al crear customer'}
            </p>
          )}
          {provisionSuccess && (
            <p className="mt-1 text-xs text-green-600">Customer Stripe creado</p>
          )}
        </div>
      ) : (
        // Account has Stripe Customer — show payment link button
        <div>
          <button
            type="button"
            onClick={handleGenerateLink}
            disabled={paymentLinkMutation.isPending}
            className="rounded-md border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {paymentLinkMutation.isPending ? '...' : 'Generar link de pago'}
          </button>
          {paymentLinkMutation.isError && (
            <p className="mt-1 text-xs text-red-600">
              {paymentLinkMutation.error?.message ?? 'Error al generar link'}
            </p>
          )}
          {paymentUrl && (
            <div className="mt-2 flex items-center gap-2 rounded-md bg-gray-50 p-2">
              <input
                readOnly
                value={paymentUrl}
                className="min-w-0 flex-1 truncate rounded border border-gray-200 bg-white px-2 py-1 font-mono text-xs text-gray-700"
              />
              <button
                type="button"
                onClick={handleCopy}
                className="flex-shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
              >
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Credit Grant Form ─────────────────────────────────────────────────────────

interface CreditGrantFormProps {
  accounts: AccountRow[]
  clientId: string
}

function CreditGrantForm({ accounts, clientId }: CreditGrantFormProps) {
  const grantCreditMutation = useGrantCredit(clientId)

  const accountsWithStripe = accounts.filter((a) => !!a.stripe_customer_id)

  const [open, setOpen] = useState(false)
  const [accountId, setAccountId] = useState(accountsWithStripe[0]?.id ?? '')
  const [amountCents, setAmountCents] = useState(0)
  const [currency, setCurrency] = useState('usd')
  const [description, setDescription] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccess(false)
    try {
      await grantCreditMutation.mutateAsync({ accountId, amountCents, currency, description })
      setSuccess(true)
      setAmountCents(0)
      setDescription('')
      setTimeout(() => {
        setOpen(false)
        setSuccess(false)
      }, 2000)
    } catch {
      // Error displayed via mutation.error
    }
  }

  if (accountsWithStripe.length === 0) {
    return null
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-sm font-medium text-gray-900 hover:text-blue-600"
      >
        <span>Otorgar credito</span>
        <span className="text-gray-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {grantCreditMutation.isError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {grantCreditMutation.error?.message ?? 'Error al otorgar credito'}
            </div>
          )}
          {success && (
            <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
              Credito otorgado exitosamente
            </div>
          )}

          {/* Account selector */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Cuenta <span className="text-red-500">*</span>
            </label>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {accountsWithStripe.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>

          {/* Amount in cents */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Monto (en centavos) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              value={amountCents}
              onChange={(e) => setAmountCents(parseInt(e.target.value, 10) || 0)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-0.5 text-xs text-gray-400">
              Ejemplo: 5000 = $50.00 USD
            </p>
          </div>

          {/* Currency */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Moneda</label>
            <input
              type="text"
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toLowerCase())}
              placeholder="usd"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Descripcion <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              placeholder="Ej. Ajuste comercial Q1"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={grantCreditMutation.isPending}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={grantCreditMutation.isPending || !accountId || amountCents <= 0}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {grantCreditMutation.isPending ? 'Otorgando...' : 'Otorgar credito'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

// ── Page component ────────────────────────────────────────────────────────────

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>()
  const clientId = id ?? ''

  const { user } = useAuth()
  const { data: client, isLoading: clientLoading, isError: clientError } = useClient(clientId)
  const { data: accounts = [], isLoading: accountsLoading } = useAccounts(clientId)

  const updateClientMutation = useUpdateClient()
  const createAccountMutation = useCreateAccount(clientId)

  // Determine admin access
  const isAdmin =
    user?.role === 'super_admin' || user?.role === 'sales_admin'

  // Billing data — only fetched for admins
  const { data: invoices = [], isLoading: invoicesLoading } = useClientInvoices(
    isAdmin ? clientId : '',
  )

  // Edit client modal
  const [showEditModal, setShowEditModal] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // New account modal
  const [showNewAccountModal, setShowNewAccountModal] = useState(false)
  const [newAccountError, setNewAccountError] = useState<string | null>(null)

  const handleUpdateClient = async (data: UpdateClientDTO) => {
    setEditError(null)
    try {
      await updateClientMutation.mutateAsync({ id: clientId, data })
      setShowEditModal(false)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al actualizar el cliente'
      setEditError(message)
    }
  }

  const handleCreateAccount = async (data: CreateAccountDTO) => {
    setNewAccountError(null)
    try {
      await createAccountMutation.mutateAsync(data)
      setShowNewAccountModal(false)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear la cuenta'
      setNewAccountError(message)
    }
  }

  // Determine if the current user can edit this client
  // Admin roles can always edit; others can only edit their own clients
  const canEdit =
    user?.role === 'super_admin' ||
    user?.role === 'sales_admin' ||
    (client && user && client.owner_id === user.id)

  // Build account name lookup for invoice table
  const accountNames: Record<string, string> = accounts.reduce(
    (acc, a) => ({ ...acc, [a.id]: a.name }),
    {} as Record<string, string>,
  )

  // ── Loading state ─────────────────────────────────────────────────────────

  if (clientLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">Cargando cliente...</p>
      </div>
    )
  }

  if (clientError || !client) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <p className="text-red-500">Error al cargar el cliente</p>
        <Link to="/clients" className="mt-4 text-sm text-blue-600 hover:text-blue-800">
          &larr; Volver a clientes
        </Link>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-6">
        <Link to="/clients" className="mb-2 inline-block text-sm text-blue-600 hover:text-blue-800">
          &larr; Volver a clientes
        </Link>

        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
          <StatusBadge status={client.status} />
          {canEdit && (
            <button
              onClick={() => {
                setEditError(null)
                setShowEditModal(true)
              }}
              className="ml-auto rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Editar
            </button>
          )}
        </div>
      </div>

      {/* Error banner from update mutation */}
      {updateClientMutation.isError && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{updateClientMutation.error?.message}</p>
        </div>
      )}

      {/* Client info card */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Informacion del cliente</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              NIT / RUT
            </dt>
            <dd className="mt-1 text-sm text-gray-900">{client.tax_id ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Contacto
            </dt>
            <dd className="mt-1 text-sm text-gray-900">{client.contact_name}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Email</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <a
                href={`mailto:${client.contact_email}`}
                className="text-blue-600 hover:text-blue-800"
              >
                {client.contact_email}
              </a>
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Telefono
            </dt>
            <dd className="mt-1 text-sm text-gray-900">{client.contact_phone ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Canal</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {CHANNEL_LABELS[client.channel] ?? client.channel}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Propietario
            </dt>
            <dd className="mt-1 text-sm text-gray-900">{client.owner_name ?? '-'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Creado</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(client.created_at)}</dd>
          </div>
          {client.notes && (
            <div className="sm:col-span-2 lg:col-span-3">
              <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">Notas</dt>
              <dd className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{client.notes}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Accounts section */}
      <div className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Cuentas ({accountsLoading ? '...' : accounts.length})
          </h2>
          {canEdit && (
            <button
              onClick={() => {
                setNewAccountError(null)
                setShowNewAccountModal(true)
              }}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
            >
              Agregar cuenta
            </button>
          )}
        </div>

        {accountsLoading ? (
          <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white py-12">
            <p className="text-gray-500">Cargando cuentas...</p>
          </div>
        ) : accounts.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white py-12">
            <p className="text-gray-500">Sin cuentas registradas</p>
            {canEdit && (
              <button
                onClick={() => {
                  setNewAccountError(null)
                  setShowNewAccountModal(true)
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800"
              >
                + Agregar primera cuenta
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {accounts.map((account) => (
              <AccountCard key={account.id} account={account} />
            ))}
          </div>
        )}
      </div>

      {/* ── Billing section (admin only) ────────────────────────────────────── */}
      {isAdmin && (
        <div className="mb-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Facturacion</h2>

          {/* Invoice history */}
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-sm font-semibold text-gray-900">
                Historial de facturas
                {invoicesLoading && (
                  <span className="ml-2 text-xs font-normal text-gray-400">Cargando...</span>
                )}
              </h3>
            </div>
            <InvoiceTable invoices={invoices} accountNames={accountNames} />
          </div>

          {/* Account billing actions — one per account */}
          {accounts.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-sm font-semibold text-gray-900">
                Acciones de facturacion por cuenta
              </h3>
              <div className="space-y-3 divide-y divide-gray-100">
                {accounts.map((account) => (
                  <div key={account.id} className="pt-3 first:pt-0">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{account.name}</span>
                      {account.stripe_customer_id && (
                        <span className="font-mono text-xs text-gray-400">
                          {account.stripe_customer_id.slice(0, 18)}…
                        </span>
                      )}
                    </div>
                    <AccountBillingActions account={account} clientId={clientId} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Credit grant form */}
          <CreditGrantForm accounts={accounts} clientId={clientId} />
        </div>
      )}

      {/* Billing rollup (real data from API when clientId provided) */}
      <BillingRollup accounts={accounts} clientId={clientId} />

      {/* Edit client modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Editar cliente</h2>
            </div>
            <div className="max-h-[80vh] overflow-y-auto p-6">
              <ClientForm
                mode="edit"
                client={client}
                onSubmit={(data) => handleUpdateClient(data as UpdateClientDTO)}
                onCancel={() => setShowEditModal(false)}
                isSubmitting={updateClientMutation.isPending}
                error={editError}
              />
            </div>
          </div>
        </div>
      )}

      {/* New account modal */}
      {showNewAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Agregar cuenta</h2>
            </div>
            <div className="p-6">
              <NewAccountForm
                onSubmit={handleCreateAccount}
                onCancel={() => setShowNewAccountModal(false)}
                isSubmitting={createAccountMutation.isPending}
                error={newAccountError}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
